import { useEffect, useRef, type ReactNode } from "react";

/**
 * Bevestiging voor iets onomkeerbaars.
 *
 * De tekst komt bij mappen rechtstreeks van de server ("Deze map bevat 128
 * berichten. Verwijderen is definitief."). Dat is bewust: alleen de server kan
 * tellen wat er echt in zit, en zo staat er nooit een geruststellender getal op
 * het scherm dan de waarheid.
 */

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy,
  onConfirm,
  onClose,
}: ConfirmDialogProps): ReactNode {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // De focus begint op Annuleren, niet op de rode knop: bij een waarschuwing
  // hoort Enter niet de gevaarlijke kant op te wijzen.
  useEffect(() => {
    const opener = document.activeElement;
    cancelRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="sheet-backdrop" role="alertdialog" aria-modal="true" aria-label={title}>
      <button type="button" className="sheet-dismiss" aria-label="Sluiten" onClick={onClose} />

      <div className="sheet sheet--dialog">
        <div className="sheet-head">
          <h2 className="sheet-title">{title}</h2>
        </div>

        <div className="sheet-body">
          <p className="confirm-text">{message}</p>
        </div>

        <div className="sheet-foot sheet-foot--actions">
          <button ref={cancelRef} type="button" className="button button--ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="button button--danger" disabled={busy} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
